import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import SetupPage from './pages/SetupPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ModelsPage from './pages/ModelsPage';
import ModelDetailPage from './pages/ModelDetailPage';
import QueuePage from './pages/QueuePage';
import MachinesPage from './pages/MachinesPage';
import FilamentsPage from './pages/FilamentsPage';
import SettingsPage from './pages/SettingsPage';
import { Box, Layers, Printer, LogOut, LayoutGrid, Cylinder, ListChecks, Settings } from 'lucide-react';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--text-muted)' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  return children;
}

function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="sidebar">
      <div className="sidebar-logo">
        <Box size={26} />
        PrintVault
      </div>
      <nav className="sidebar-nav">
        <div className="nav-section">Overview</div>
        <NavLink to="/" end className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}>
          <LayoutGrid size={18} /> Dashboard
        </NavLink>

        <div className="nav-section">Library</div>
        <NavLink to="/models" className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}>
          <Layers size={18} /> Models
        </NavLink>

        <div className="nav-section">Production</div>
        <NavLink to="/queue" className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}>
          <ListChecks size={18} /> Print Queue
        </NavLink>

        <div className="nav-section">Resources</div>
        <NavLink to="/machines" className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}>
          <Printer size={18} /> Machines
        </NavLink>
        <NavLink to="/filaments" className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}>
          <Cylinder size={18} /> Filaments
        </NavLink>
      </nav>
      <div style={{ padding: '16px', borderTop: '1px solid var(--border)' }}>
        <NavLink to="/settings" className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`} style={{ marginBottom: '8px' }}>
          <Settings size={16} /> Settings
        </NavLink>
        <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '8px', padding: '0 14px' }}>{user?.display_name || user?.username}</div>
        <button onClick={handleLogout} className="nav-item" style={{ width: '100%', border: 'none', background: 'none', padding: '8px 14px' }}>
          <LogOut size={16} /> Sign Out
        </button>
      </div>
    </div>
  );
}

function AppLayout() {
  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/models" element={<ModelsPage />} />
          <Route path="/models/:id" element={<ModelDetailPage />} />
          <Route path="/queue" element={<QueuePage />} />
          <Route path="/machines" element={<MachinesPage />} />
          <Route path="/filaments" element={<FilamentsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/setup" element={<SetupPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/*" element={<ProtectedRoute><AppLayout /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
